import { handle400Statuses, sendApiRequest } from '@services/api';
import { logNetRequest } from '@utils/usefulFuncs';

const GENERATE_URL = 'v2/genai/agent_scripts/generate';
const DEFINITIONS_URL = 'v1/genai_agent_script_definitions';

/**
 * Ask the backend to generate and persist a new agent script from a
 * plain-English description. Resolves with the raw `{ status, data }` so the
 * caller can branch on status codes (200 success, 400 validation, etc.).
 */
export const generateAgentScript = async ({ name, description }) => {
  const { status, data } = await sendApiRequest('post', GENERATE_URL, { name, description });
  logNetRequest(status, data);
  handle400Statuses(status);
  return { status, data };
};

/**
 * Fetch the user's agent-script definitions. Returns an array (empty on error)
 * so callers can treat it as a list without null checks.
 */
export const fetchAgentScripts = async () => {
  const { status, data } = await sendApiRequest('get', DEFINITIONS_URL);
  logNetRequest(status, data);
  handle400Statuses(status);
  return status === 200 ? data : [];
};

/** Pure lookup: find a script by its exact, scope-unique name. */
export const findAgentScriptByName = (scripts, name) => scripts.find((script) => script.script_name === name) || null;

/** Fetch a single script record by id (used to snapshot before an improve). */
export const fetchAgentScript = async (id) => {
  const { status, data } = await sendApiRequest('get', `${DEFINITIONS_URL}/${id}`);
  logNetRequest(status, data);
  handle400Statuses(status);
  return status === 200 ? data : null;
};

/**
 * Ask the backend to rewrite an existing script's plan in place. Destructive —
 * the previous `definition_json` is overwritten. Resolves with raw
 * `{ status, data }` so the caller can branch on status (200/400/404/...).
 */
export const improveAgentScript = async (id, description) => {
  const { status, data } = await sendApiRequest('post', `v2/genai/agent_scripts/${id}/improve`, { description });
  logNetRequest(status, data);
  handle400Statuses(status);
  return { status, data };
};

/**
 * Overwrite a script's definition (used to revert to a client-side snapshot).
 * Name is preserved. Returns true on success.
 */
export const updateAgentScriptDefinition = async (id, scriptName, definition) => {
  const form = {
    id,
    definition_json: JSON.stringify(definition),
    script_name: scriptName,
  };
  const { status, data } = await sendApiRequest('patch', `${DEFINITIONS_URL}/${id}`, form);
  logNetRequest(status, data);
  handle400Statuses(status);
  return status === 200;
};

/** Normalize a `definition_json` field that may arrive as an object or a JSON string. */
export const parseDefinition = (definitionJson) =>
  typeof definitionJson === 'string' ? JSON.parse(definitionJson) : definitionJson;
